import { A, useLocation, useNavigate, useSearchParams } from "@solidjs/router";
import { AiOutlineHeart } from "solid-icons/ai";
import { FiTool } from "solid-icons/fi";
import {
  IoBriefcaseOutline,
  IoChatbubbleEllipsesOutline,
  IoCloseOutline,
  IoDocumentTextOutline,
  IoGridOutline,
  IoHelpCircle,
  IoMailOutline,
  IoMenuOutline,
  IoNewspaperOutline,
  IoPaperPlaneOutline,
  IoPersonOutline,
} from "solid-icons/io";
import {
  type Component,
  createEffect,
  createMemo,
  createSignal,
  For,
  type ParentComponent,
  Show,
} from "solid-js";
import { useI18n } from "@/api/i18n";
import compactLogo from "@/assets/compact-light-logo.svg";
import extendedLogo from "@/assets/extended-light-logo.svg";
import { Button } from "@/components/ui/button";
import { LoadingSkeleton as Skeleton } from "@/components/ui/skeleton";
import { useAuthV2 } from "@/domains/auth/context/auth-provider";
import { CoverTopBanner } from "@/domains/business/partnership/fido/components/fido";
import {
  dismissCoverBanner,
  isCoverBannerDismissed,
} from "@/domains/business/partnership/fido/store/cover-banner-dismissal";
import { SupportModal } from "@/domains/business/support/components/SupportModal";
import { InviteFreelancerModal } from "@/domains/company/invitations/components/InviteFreelancerModal";
import { AvailabilityModal } from "@/domains/freelance/availability/components/AvailabilityModal";
import { isAvailableNow } from "@/domains/freelance/availability/utils/is-available-now";
import { Container } from "../shared/Container";
import {
  AppSwitcherPopover,
  DesktopTopBar,
  ExternalNavigationItem,
  MainContentContainer,
  MobileOverlay,
  MobileTopBar,
  Sidebar,
  SidebarActionItem,
  SidebarNav,
  SidebarNavigationItem,
  SidebarNavigationTitle,
  SidebarSection,
} from "../shared/Navigation";
import { Card, CardContent } from "../ui/card";
import { LayoutProvider, useLayoutContext } from "./context/LayoutContext";

const NOT_PROFILE_PATHS = [
  "/profile/toolkit",
  "/profile/partners",
  "/profile/applications",
  "/profile/proposals",
] as const;

const isNotProfilePath = (path: string) => {
  return NOT_PROFILE_PATHS.some((notProfilePath) =>
    path.startsWith(notProfilePath),
  );
};

const ProfileSidebar: ParentComponent = (props) => {
  const {
    isMobileMenuOpen,
    setIsSupportModalOpen,
    openAvailabilityModal,
    toggleMobileMenu,
    handleOpenDonation,
  } = useLayoutContext();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [authStoreV2] = useAuthV2();
  const { t } = useI18n();

  createEffect(() => {
    const openRecommend = searchParams.openRecommend;
    if (openRecommend === "1") {
      navigate("/profile?tab=recommendations", { replace: true });
    }
  });

  const publicNavigationItems = createMemo(() => [
    {
      name: t("profile.layout.navigation.findWork"),
      href: "/",
      icon: IoBriefcaseOutline,
      active: location.pathname === "/",
    },
  ]);

  const profileNavigationItems = createMemo(() => [
    {
      // First, because it is where a freelancer now lands after login and the question
      // it answers («mi sta cercando qualcuno?») is the one they arrive with. It sits
      // outside `/profile`, so `NOT_PROFILE_PATHS` needs no entry: «Profilo» simply does
      // not match this pathname.
      name: t("profile.layout.navigation.dashboard"),
      href: "/freelance/dashboard",
      icon: IoGridOutline,
      active: location.pathname.startsWith("/freelance/dashboard"),
    },
    {
      name: t("profile.layout.navigation.profile"),
      href: "/profile",
      icon: IoPersonOutline,
      active:
        location.pathname.startsWith("/profile") &&
        !isNotProfilePath(location.pathname),
    },
    {
      name: t("profile.layout.navigation.applications"),
      href: "/profile/applications",
      icon: IoDocumentTextOutline,
      active: location.pathname.startsWith("/profile/applications"),
    },
    {
      name: t("profile.layout.navigation.proposals"),
      href: "/profile/proposals",
      icon: IoMailOutline,
      active: location.pathname.startsWith("/profile/proposals"),
    },
    {
      name: t("profile.layout.navigation.toolkit"),
      href: "/profile/toolkit",
      icon: FiTool,
      active: location.pathname.startsWith("/profile/toolkit"),
    },
  ]);

  const partnershipItems = createMemo(() => [
    {
      name: t("profile.layout.navigation.promotions"),
      href: "/profile/partners",
      icon: IoNewspaperOutline,
      active: location.pathname.startsWith("/profile/partners"),
    },
  ]);

  return (
    <div class="min-h-screen bg-gray-50">
      {/* Mobile Header */}
      <MobileTopBar>
        <div class="flex items-center gap-3">
          <img
            src={compactLogo}
            alt="Sevedemo logo"
            class="w-auto h-8 block md:hidden"
          />
          <img
            src={extendedLogo}
            alt="Sevedemo logo"
            class="w-auto h-6 hidden md:block"
          />

          {/* <div class="flex-1 max-w-xs">
            <div class="relative">
              <IoSearchOutline class="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                type="text"
                placeholder={t("profile.layout.searchPlaceholder")}
                class="pl-10 bg-white/10 border-white/20 text-white placeholder:text-gray-300 focus-visible:ring-white/50"
              />
            </div>
          </div> */}
        </div>
        <div class="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsSupportModalOpen(true)}
          >
            <IoHelpCircle class="w-5 h-5" />
          </Button>
          <Show when={authStoreV2?.profile?.weekly_hours}>
            <Button
              variant="outline"
              size="sm"
              onClick={() => openAvailabilityModal("navigation_bar")}
            >
              <Show
                when={isAvailableNow(authStoreV2?.profile?.available_from)}
                fallback={t("profile.layout.addAvailability")}
              >
                {authStoreV2?.profile?.weekly_hours ?? "40"}{" "}
                {t("availability.h_weekly_hours")}
              </Show>
            </Button>
          </Show>
          <Show when={!authStoreV2?.profile?.weekly_hours}>
            <Button
              variant="outline"
              size="sm"
              onClick={() => openAvailabilityModal("navigation_bar")}
            >
              {t("profile.layout.addAvailability")}
            </Button>
          </Show>
          <Button
            variant="outline"
            size="sm"
            onClick={toggleMobileMenu}
            aria-label={
              isMobileMenuOpen()
                ? t("layout.common.closeMenu")
                : t("layout.common.openMenu")
            }
            aria-expanded={isMobileMenuOpen()}
            aria-controls="mobile-menu"
          >
            <Show
              when={isMobileMenuOpen()}
              fallback={<IoMenuOutline class="w-6 h-6" />}
            >
              <IoCloseOutline class="w-6 h-6" />
            </Show>
          </Button>
        </div>
      </MobileTopBar>

      {/* Desktop TopBar */}
      <DesktopTopBar>
        <div class="flex items-center gap-4 flex-1">
          <A
            href="/"
            class="w-fit h-10 bg-white rounded flex items-center justify-center"
          >
            <img
              src={compactLogo}
              alt="Sevedemo logo"
              class="w-auto h-8 block md:hidden"
            />
            <img
              src={extendedLogo}
              alt="Sevedemo logo"
              class="w-auto h-6 hidden md:block"
            />
          </A>
          {/* <div class="flex-1 max-w-md">
            <div class="relative">
              <IoSearchOutline class="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                type="text"
                placeholder="Search a profile"
                class="pl-10 bg-white/10 border-white/20 text-white placeholder:text-gray-300 focus-visible:ring-white/50"
              />
            </div>
          </div> */}
        </div>
        <div class="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsSupportModalOpen(true)}
          >
            <IoHelpCircle class="w-5 h-5" />
          </Button>
          <Show when={authStoreV2?.profile?.weekly_hours}>
            <Button
              variant="outline"
              size="sm"
              onClick={() => openAvailabilityModal("navigation_bar")}
            >
              <Show
                when={isAvailableNow(authStoreV2?.profile?.available_from)}
                fallback={t("profile.layout.addAvailability")}
              >
                {authStoreV2?.profile?.weekly_hours ?? "40"}{" "}
                {t("availability.h_weekly_hours")}
              </Show>
            </Button>
          </Show>
          <Show when={!authStoreV2?.profile?.weekly_hours}>
            <Button
              variant="outline"
              size="sm"
              onClick={() => openAvailabilityModal("navigation_bar")}
            >
              {t("profile.layout.addAvailability")}
            </Button>
          </Show>
        </div>
      </DesktopTopBar>

      {/* Mobile Overlay */}
      <MobileOverlay />

      <div class="flex">
        {/* Sidebar */}
        <Sidebar>
          {/* Navigation */}
          <SidebarNav class="overflow-y-auto">
            <div class="flex flex-col gap-1 mt-4">
              <For each={publicNavigationItems()}>
                {(item) => <ExternalNavigationItem {...item} />}
              </For>
            </div>
            <SidebarSection class="flex flex-col gap-1">
              <SidebarNavigationTitle>
                {t("layout.common.profile")}
              </SidebarNavigationTitle>
              <For each={profileNavigationItems()}>
                {(item) => <SidebarNavigationItem {...item} />}
              </For>
            </SidebarSection>
            <SidebarSection class="flex flex-col gap-1">
              <SidebarNavigationTitle>
                {t("profile.layout.navigation.collaborations")}
              </SidebarNavigationTitle>
              <For each={partnershipItems()}>
                {(item) => <SidebarNavigationItem {...item} />}
              </For>
              <InviteFreelancerButtonWrapper />
            </SidebarSection>
            <SidebarSection>
              <SidebarNavigationTitle>
                {t("layout.common.helpCenter")}
              </SidebarNavigationTitle>
              <div class="flex flex-col gap-2">
                <SidebarActionItem
                  name={t("layout.common.contactSupport")}
                  icon={IoChatbubbleEllipsesOutline}
                  onClick={() => setIsSupportModalOpen(true)}
                />
                <SidebarActionItem
                  name={t("layout.common.donate")}
                  icon={AiOutlineHeart}
                  onClick={handleOpenDonation}
                />
              </div>
            </SidebarSection>
          </SidebarNav>

          {/* User Profile Section at Bottom */}
          <AppSwitcherPopover type="consumer" />
        </Sidebar>

        {/* Main Content */}
        <MainContentContainer>{props.children}</MainContentContainer>
      </div>

      {/* Support Modal */}
      <SupportModal />

      {/* Availability Modal */}
      <AvailabilityModal />

      {/* Invite Freelancer Modal */}
      <InviteFreelancerModalWrapper />
    </div>
  );
};

const InviteFreelancerButtonWrapper = () => {
  const { t } = useI18n();
  const { setIsInviteFreelancerModalOpen } = useLayoutContext();

  return (
    <SidebarActionItem
      name={t("freelancerInvitations.recommendColleague")}
      icon={IoPaperPlaneOutline}
      onClick={() => setIsInviteFreelancerModalOpen(true)}
    />
  );
};

const InviteFreelancerModalWrapper = () => {
  const { isInviteFreelancerModalOpen, setIsInviteFreelancerModalOpen } =
    useLayoutContext();

  return (
    <InviteFreelancerModal
      isOpen={isInviteFreelancerModalOpen()}
      onClose={() => setIsInviteFreelancerModalOpen(false)}
    />
  );
};

export const ProfileSidebarLayout: ParentComponent = (props) => {
  return (
    <LayoutProvider sidebarStorageKey="sidebar_profile">
      <ProfileSidebar>{props.children}</ProfileSidebar>
    </LayoutProvider>
  );
};

/**
 * The content column of the freelancer's pages, with the partner banner on top.
 *
 * The banner's close is a fact of the browser (B4): read **synchronously once per mount**,
 * so a closed banner is never drawn and removed (B5), and re-read on every mount, so a
 * close made in another tab counts at the next page change (EC-FB3). After the X the
 * focus goes to the content — not to an `h1`, which `/profile` does not have (B6).
 */
export const ProfileLayoutMainContentContainer: ParentComponent = (props) => {
  const [bannerDismissed, setBannerDismissed] = createSignal(
    isCoverBannerDismissed(new Date()),
  );
  let content: HTMLDivElement | undefined;

  const dismissBanner = () => {
    dismissCoverBanner(new Date());
    setBannerDismissed(true);
    content?.focus();
  };

  return (
    <Container class="px-0! lg:px-6!" size="full">
      <Show when={!bannerDismissed()}>
        <CoverTopBanner class="mb-6" onDismiss={dismissBanner} />
      </Show>
      <div ref={content} tabindex="-1" class="outline-none">
        {props.children}
      </div>
    </Container>
  );
};

export const ProfileLayoutSkeleton: Component = () => {
  return (
    <LayoutProvider sidebarStorageKey="sidebar_profile">
      <div class="min-h-screen bg-gray-50">
        {/* Mobile TopBar Skeleton */}
        <MobileTopBar>
          <div class="flex items-center gap-3">
            <Skeleton
              width={40}
              height={40}
              class="rounded-md animate-pulse bg-gray-200"
            />
          </div>
          <div class="flex items-center gap-2">
            <Skeleton
              width={36}
              height={36}
              class="rounded animate-pulse bg-gray-200"
            />
            <Skeleton
              width={128}
              height={36}
              class="rounded animate-pulse bg-gray-200"
            />
          </div>
        </MobileTopBar>

        {/* Desktop TopBar Skeleton */}
        <DesktopTopBar>
          <div class="flex items-center gap-4 flex-1">
            <Skeleton
              width={120}
              height={40}
              class="rounded animate-pulse bg-gray-200"
            />
          </div>
          <div class="flex items-center gap-2">
            <Skeleton
              width={36}
              height={36}
              class="rounded animate-pulse bg-gray-200"
            />
            <Skeleton
              width={120}
              height={36}
              class="rounded animate-pulse bg-gray-200"
            />
          </div>
        </DesktopTopBar>

        <div class="flex">
          {/* Sidebar Skeleton */}
          <Sidebar>
            <SidebarNav class="overflow-y-auto">
              {/* Public Navigation Item Skeleton */}
              <div class="flex flex-col gap-1 mt-4">
                <Skeleton
                  width={240}
                  height={44}
                  class="rounded-lg animate-pulse bg-gray-200"
                />
              </div>

              {/* Profile Section Title Skeleton */}
              <div class="flex flex-col gap-1 mt-8">
                <Skeleton
                  width={96}
                  height={16}
                  class="mb-4 rounded animate-pulse bg-gray-200"
                />
                <Skeleton
                  width={240}
                  height={36}
                  class="rounded-lg animate-pulse bg-gray-200"
                />
                <Skeleton
                  width={240}
                  height={36}
                  class="rounded-lg animate-pulse bg-gray-200"
                />
              </div>

              {/* Help Center Section Skeleton */}
              <div class="mt-8">
                <Skeleton
                  width={128}
                  height={16}
                  class="mb-4 rounded animate-pulse bg-gray-200"
                />
                <div class="flex flex-col gap-2">
                  <Skeleton
                    width={240}
                    height={40}
                    class="rounded animate-pulse bg-gray-200"
                  />
                  <Skeleton
                    width={240}
                    height={40}
                    class="rounded animate-pulse bg-gray-200"
                  />
                </div>
              </div>
            </SidebarNav>

            {/* User Profile Section at Bottom Skeleton */}
            <div class="px-4 py-4 border-t border-gray-200">
              <div class="flex items-center gap-3">
                <Skeleton
                  width={32}
                  height={32}
                  class="rounded-full animate-pulse bg-gray-200"
                />
                <div class="flex-1 space-y-2">
                  <Skeleton
                    width={96}
                    height={16}
                    class="rounded animate-pulse bg-gray-200"
                  />
                  <Skeleton
                    width={80}
                    height={12}
                    class="rounded animate-pulse bg-gray-200"
                  />
                </div>
                <Skeleton
                  width={16}
                  height={16}
                  class="rounded animate-pulse bg-gray-200"
                />
              </div>
            </div>
          </Sidebar>

          {/* Main Content Skeleton */}
          <MainContentContainer>
            <Container class="px-0! lg:px-6!" size="full">
              <div class="w-full">
                {/* Two-column layout: lateral card on left, tabs on right */}
                <div class="flex flex-col lg:flex-row gap-6">
                  {/* Lateral Card Skeleton - Hidden on mobile, visible on desktop */}
                  <div class="hidden lg:block lg:w-80 lg:shrink-0">
                    <Card class="w-full shadow-none sticky top-4">
                      <CardContent class="p-6 space-y-6">
                        {/* Header Section Skeleton */}
                        <div class="space-y-4">
                          <div class="flex items-start justify-between">
                            <div class="flex items-center gap-3">
                              <Skeleton
                                width={64}
                                height={64}
                                class="rounded-full animate-pulse bg-gray-200"
                              />
                              <div class="space-y-2">
                                <Skeleton
                                  width={128}
                                  height={20}
                                  class="rounded animate-pulse bg-gray-200"
                                />
                                <Skeleton
                                  width={160}
                                  height={16}
                                  class="rounded animate-pulse bg-gray-200"
                                />
                              </div>
                            </div>
                            <Skeleton
                              width={32}
                              height={32}
                              class="rounded animate-pulse bg-gray-200"
                            />
                          </div>
                        </div>

                        {/* Intro Section Skeleton */}
                        <div class="space-y-2">
                          <Skeleton
                            width={96}
                            height={16}
                            class="rounded animate-pulse bg-gray-200"
                          />
                          <div class="space-y-2">
                            <Skeleton
                              width={280}
                              height={16}
                              class="rounded animate-pulse bg-gray-200"
                            />
                            <Skeleton
                              width={280}
                              height={16}
                              class="rounded animate-pulse bg-gray-200"
                            />
                            <Skeleton
                              width={210}
                              height={16}
                              class="rounded animate-pulse bg-gray-200"
                            />
                          </div>
                        </div>

                        {/* Work Preferences Section Skeleton */}
                        <div class="space-y-3">
                          <div class="flex items-center justify-between">
                            <Skeleton
                              width={128}
                              height={16}
                              class="rounded animate-pulse bg-gray-200"
                            />
                            <Skeleton
                              width={24}
                              height={24}
                              class="rounded animate-pulse bg-gray-200"
                            />
                          </div>
                          <div class="space-y-2">
                            <Skeleton
                              width={280}
                              height={16}
                              class="rounded animate-pulse bg-gray-200"
                            />
                            <Skeleton
                              width={280}
                              height={16}
                              class="rounded animate-pulse bg-gray-200"
                            />
                            <Skeleton
                              width={280}
                              height={16}
                              class="rounded animate-pulse bg-gray-200"
                            />
                            <Skeleton
                              width={280}
                              height={16}
                              class="rounded animate-pulse bg-gray-200"
                            />
                          </div>
                        </div>

                        {/* Personal Data Section Skeleton */}
                        <div class="space-y-3">
                          <div class="flex items-center justify-between">
                            <Skeleton
                              width={112}
                              height={16}
                              class="rounded animate-pulse bg-gray-200"
                            />
                            <Skeleton
                              width={24}
                              height={24}
                              class="rounded animate-pulse bg-gray-200"
                            />
                          </div>
                          <div class="space-y-2">
                            <Skeleton
                              width={280}
                              height={16}
                              class="rounded animate-pulse bg-gray-200"
                            />
                            <Skeleton
                              width={280}
                              height={16}
                              class="rounded animate-pulse bg-gray-200"
                            />
                          </div>
                        </div>

                        {/* Expertises Section Skeleton */}
                        <div class="space-y-3">
                          <div class="flex items-center justify-between">
                            <Skeleton
                              width={128}
                              height={16}
                              class="rounded animate-pulse bg-gray-200"
                            />
                            <Skeleton
                              width={24}
                              height={24}
                              class="rounded animate-pulse bg-gray-200"
                            />
                          </div>
                          <div class="flex flex-wrap gap-2">
                            <For each={Array(3)}>
                              {() => (
                                <Skeleton
                                  width={80}
                                  height={24}
                                  class="rounded-full animate-pulse bg-gray-200"
                                />
                              )}
                            </For>
                          </div>
                        </div>

                        {/* Languages Section Skeleton */}
                        <div class="space-y-3">
                          <Skeleton
                            width={96}
                            height={16}
                            class="rounded animate-pulse bg-gray-200"
                          />
                          <div class="space-y-2">
                            <Skeleton
                              width={280}
                              height={16}
                              class="rounded animate-pulse bg-gray-200"
                            />
                            <Skeleton
                              width={280}
                              height={16}
                              class="rounded animate-pulse bg-gray-200"
                            />
                          </div>
                        </div>

                        {/* Contact Section Skeleton */}
                        <div class="space-y-3">
                          <div class="flex items-center justify-between">
                            <Skeleton
                              width={80}
                              height={16}
                              class="rounded animate-pulse bg-gray-200"
                            />
                            <Skeleton
                              width={24}
                              height={24}
                              class="rounded animate-pulse bg-gray-200"
                            />
                          </div>
                          <div class="flex flex-wrap gap-3">
                            <For each={Array(4)}>
                              {() => (
                                <Skeleton
                                  width={40}
                                  height={40}
                                  class="rounded-full animate-pulse bg-gray-200"
                                />
                              )}
                            </For>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Main Content Area Skeleton */}
                  <div class="flex-1 min-w-0 bg-card rounded-xl border p-4 space-y-6">
                    {/* Profile Header Skeleton */}
                    <div class="flex items-center justify-between gap-2 md:gap-0">
                      <div class="flex items-center gap-4">
                        <div class="flex items-center gap-3">
                          <Skeleton
                            width={40}
                            height={40}
                            class="rounded-full animate-pulse bg-gray-200"
                          />
                          <div class="space-y-2">
                            <Skeleton
                              width={96}
                              height={20}
                              class="rounded animate-pulse bg-gray-200"
                            />
                            <Skeleton
                              width={80}
                              height={16}
                              class="rounded animate-pulse bg-gray-200"
                            />
                          </div>
                        </div>
                      </div>
                      <Skeleton
                        width={128}
                        height={40}
                        class="rounded animate-pulse bg-gray-200"
                      />
                    </div>

                    {/* Tabs wrapped in Card Skeleton */}
                    <Card>
                      <CardContent class="p-0">
                        <div class="overflow-x-auto lg:grid lg:w-full lg:grid-cols-3 lg:justify-start flex flex-nowrap justify-between max-w-2xl md:max-w-full lg:max-w-none border-0 p-2">
                          <For each={Array(3)}>
                            {() => (
                              <Skeleton
                                width={96}
                                height={40}
                                class="rounded-md animate-pulse bg-gray-200"
                              />
                            )}
                          </For>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Mobile GeneralInfoCard Skeleton - Shown on mobile overview tab */}
                    <div class="lg:hidden mb-6">
                      <Card class="w-full shadow-none">
                        <CardContent class="p-6 space-y-6">
                          <div class="flex items-start justify-between">
                            <div class="flex items-center gap-3">
                              <Skeleton class=" rounded-full w-full h-10" />
                              <div class="space-y-2">
                                <Skeleton class="rounded w-full h-10" />
                                <Skeleton class="rounded w-full h-10" />
                              </div>
                            </div>
                            <Skeleton width={32} height={32} class="rounded" />
                          </div>
                          <div class="space-y-2">
                            <Skeleton class="rounded w-full h-10" />
                            <Skeleton class="rounded w-full h-10" />
                            <Skeleton class="rounded w-full h-10" />
                            <Skeleton class="rounded w-full h-10" />
                          </div>
                        </CardContent>
                      </Card>
                    </div>

                    {/* Tab Content Skeleton - Curriculum Tab */}
                    <div class="space-y-6">
                      <Card class="shadow-none">
                        <CardContent class="p-0">
                          <div class="flex items-center justify-between p-6 border-b border-gray-200">
                            <div class="flex items-center gap-3">
                              <Skeleton class="rounded w-full h-10" />
                              <Skeleton class="rounded w-full h-10" />
                            </div>
                            <Skeleton class="rounded w-full h-10" />
                          </div>

                          {/* Resume File Skeleton */}
                          <div class="p-6 space-y-4">
                            <Card class="shadow-none">
                              <CardContent class="p-6">
                                <div class="flex items-center justify-between">
                                  <div class="flex items-center gap-4">
                                    <Skeleton class="rounded-lg w-full h-10" />
                                    <div class="space-y-2">
                                      <Skeleton class="rounded w-full h-10" />
                                      <Skeleton class="rounded w-full h-10" />
                                    </div>
                                  </div>
                                  <div class="flex items-center gap-2">
                                    <Skeleton class="rounded w-full h-10" />
                                    <Skeleton class="rounded w-full h-10" />
                                  </div>
                                </div>
                              </CardContent>
                            </Card>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  </div>
                </div>
              </div>
            </Container>
          </MainContentContainer>
        </div>
      </div>
    </LayoutProvider>
  );
};
